'use client';

import type { UpdateBlockStateType } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import type { CardBlockStateType } from '@unifyapps/defs/types/blocks';
import { useEffectAfterMount } from '@unifyapps/hooks/useEffectAfterMount';

/**
 * Syncs collapsible content config (isInitiallyExpanded) to block state.
 * Rendered only in builder mode so the effect runs only there.
 */
export function CardBuilderCollapsibleSync({
  blockState,
  updateBlockState,
  setExpandedRef,
  isProgrammaticCollapseChangeRef,
}: {
  blockState: CardBlockStateType;
  updateBlockState: UpdateBlockStateType<CardBlockStateType>;
  setExpandedRef: React.RefObject<((expanded: boolean) => void) | null>;
  isProgrammaticCollapseChangeRef: React.RefObject<boolean>;
}) {
  const isCollapsable = Boolean(blockState.content.collapsible?.enabled);

  useEffectAfterMount(() => {
    const newIsCollapsed = !(blockState.content.collapsible?.isInitiallyExpanded ?? true);

    if (blockState.content.collapsible?.enabled) {
      // mark so CardLayout's onTransitionStateChange skips calling setIsCollapsed
      isProgrammaticCollapseChangeRef.current = true;
      setExpandedRef.current?.(!newIsCollapsed);
      if (Boolean(blockState.isCollapsed) !== newIsCollapsed) {
        updateBlockState((draft) => {
          draft.isCollapsed = newIsCollapsed;
          if (!draft.appearance) draft.appearance = { isCollapsed: false };
          draft.appearance.isCollapsed = newIsCollapsed;
        });
      }
      isProgrammaticCollapseChangeRef.current = false;
    } else {
      isProgrammaticCollapseChangeRef.current = true;
      setExpandedRef.current?.(true);
      updateBlockState((draft) => {
        draft.isCollapsed = false;
        if (!draft.appearance) draft.appearance = { isCollapsed: false };
        draft.appearance.isCollapsed = false;
      });
      isProgrammaticCollapseChangeRef.current = false;
    }
  }, [blockState.content.collapsible?.isInitiallyExpanded, isCollapsable]);

  return null;
}
